/* eslint-disable camelcase */
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { createPortal } from "react-dom";

const LoadingCtx = createContext(null);

const SHOW_DELAY_MS = 500; // show only if work lasts > 0.5s
const MIN_SHOW_MS = 400;   // keep visible briefly to avoid flicker
export const MAX_LOADING_MS = 15000;
export const LOADING_OWNERS = Object.freeze({
  ROUTE_LOADING: "ROUTE_LOADING",
  CHECKOUT_HANDOFF: "CHECKOUT_HANDOFF",
  EXTERNAL_PAYMENT_HANDOFF: "EXTERNAL_PAYMENT_HANDOFF",
  CHECKOUT_BOOT: "CHECKOUT_BOOT",
});

export function LoadingProvider({ children, autoTrackFetch = false }) {
  const pendingRef = useRef(0);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const generationRef = useRef(0);
  const shownAtRef = useRef(0);
  const shownRef     = useRef(false);
  const ownerRef = useRef(LOADING_OWNERS.ROUTE_LOADING);
  const [visible, setVisible] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    generationRef.current += 1;
    pendingRef.current = 0;
    shownRef.current = false;
    shownAtRef.current = 0;
    clearTimers();
    setPendingCount(0);
    setVisible(false);
    setTimedOut(false);
    ownerRef.current = LOADING_OWNERS.ROUTE_LOADING;
  }, [clearTimers]);

  const transferOwnership = useCallback((owner = LOADING_OWNERS.ROUTE_LOADING) => {
    generationRef.current += 1;
    pendingRef.current = 0;
    shownRef.current = false;
    shownAtRef.current = 0;
    ownerRef.current = owner;
    clearTimers();
    setPendingCount(0);
    setVisible(false);
    setTimedOut(false);
  }, [clearTimers]);

  // stable stop(): uses refs, so it won't close over stale visible
  const stop = useCallback(() => {
    if (pendingRef.current > 0) {
      pendingRef.current -= 1;
      setPendingCount(pendingRef.current);
    }

    if (pendingRef.current === 0) {
      // If we never actually showed, just cancel any pending show
      if (!shownRef.current) {
        // was never shown → just cancel the delayed show
        clearTimers();
        } else {
        const elapsed   = Date.now() - shownAtRef.current;
        const remaining = Math.max(0, MIN_SHOW_MS - elapsed);
        hideTimerRef.current = setTimeout(() => {
          shownRef.current  = false;
          shownAtRef.current = 0;
          setVisible(false);
          clearTimers();
        }, remaining);
      }}
  }, [clearTimers]);

  // start() depends on stop so it returns a correct "done" function
  const start = useCallback((options = {}) => {
    const owner = options.owner || LOADING_OWNERS.ROUTE_LOADING;
    const timeoutMs = Number(options.timeoutMs || MAX_LOADING_MS);
    const routeTimeoutEnabled = options.routeTimeout !== false && owner === LOADING_OWNERS.ROUTE_LOADING;
    if (pendingRef.current === 0) clearTimers();
    ownerRef.current = owner;
    setTimedOut(false);
    const generation = generationRef.current;
    let finished = false;
    pendingRef.current += 1;
    setPendingCount(pendingRef.current);

    // first task → arm delayed show
    if (pendingRef.current === 1) {
      showTimerRef.current = setTimeout(() => {
        shownRef.current   = true;
        shownAtRef.current = Date.now();
        setVisible(true);
      }, SHOW_DELAY_MS);
      if (routeTimeoutEnabled) {
        maxTimerRef.current = setTimeout(() => {
          if (ownerRef.current !== LOADING_OWNERS.ROUTE_LOADING) return;
          generationRef.current += 1;
          pendingRef.current = 0;
          shownRef.current = false;
          setPendingCount(0);
          setVisible(false);
          setTimedOut(true);
          clearTimers();
        }, timeoutMs);
      }
    }

    // return a stopper for convenience
    return () => {
      if (finished || generation !== generationRef.current) return;
      finished = true;
      stop();
    };
  }, [clearTimers, stop]);

  // Wrap a promise or a thunk returning a promise
  const withGlobalLoading = useCallback(
    (work, options = {}) => {
      const done = start(options);
      try {
        const p = typeof work === "function" ? work() : work;
        return Promise.resolve(p).finally(done);
      } catch (err) {
        done();
        throw err;
      }
    },
    [start]
  );

  // OPTIONAL: auto-track fetch (off by default to avoid tracking uploads, etc.)
  useEffect(() => {
    if (!autoTrackFetch || !window.fetch) return;
    const orig = window.fetch;
    window.fetch = (...args) => {
      const done = start();
      return orig(...args).finally(done);
    };
    return () => {
      window.fetch = orig;
    };
  }, [start, autoTrackFetch]);

  const value = useMemo(
    () => ({
      visible,
      pendingCount,
      start,
      stop,
      withGlobalLoading,
      reset,
      transferOwnership,
    }),
    [visible, pendingCount, start, stop, withGlobalLoading, reset, transferOwnership]
  );

  return (
    <LoadingCtx.Provider value={value}>
      {children}
      <GlobalLoader visible={visible} timedOut={timedOut} onRetry={() => window.location.reload()} />
    </LoadingCtx.Provider>
  );
}

export function useGlobalLoading() {
  const ctx = useContext(LoadingCtx);
  if (!ctx) throw new Error("useGlobalLoading must be used within <LoadingProvider>");
  return ctx;
}

/* ---------- UI overlay ---------- */
function GlobalLoader({ visible, timedOut, onRetry }) {
  if (!visible && !timedOut) return null;
  const node = (
    <div
      role="status"
      aria-live="polite"
      aria-busy={visible ? "true" : "false"}
      style={overlayStyle}
      data-qa="global-loader"
    >
      {visible ? <><div style={spinnerStyle} /><div style={labelStyle}>Loading…</div></> :
        <div style={{textAlign: "center", color: "white"}}><strong>That page took too long to load.</strong><br />
          <button type="button" onClick={onRetry} style={{marginTop: 12}}>Retry</button></div>}
    </div>
  );
  // render at <body> level so it always floats above everything
  return createPortal(node, document.body);
}

/* Inline styles to avoid extra files (you can move to CSS later) */
const overlayStyle = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.28)",
  backdropFilter: "blur(2px)",
  zIndex: 2147483647, // max-ish, above all modals
};

const spinnerStyle = {
  width: 46,
  height: 46,
  border: "4px solid rgba(255,255,255,0.25)",
  borderTopColor: "#21d7c5",
  borderRadius: "50%",
  animation: "vkz-spin 0.9s linear infinite",
};

const labelStyle = {
  marginTop: 12,
  color: "gold",
  fontWeight: 600,
  fontSize: 14,
};

/* Inject a simple keyframes rule once */
const styleEl =
  typeof document !== "undefined" ? document.createElement("style") : null;
if (styleEl) {
  styleEl.innerHTML = `
@keyframes vkz-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;
  document.head.appendChild(styleEl);
}
